import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSettings, FiCheck, FiArrowLeft, FiMail, FiUsers, FiFolder, FiZap } = FiIcons;

const MicrosoftAutomation = () => {
  const features = [
    {
      icon: FiZap,
      title: 'Power Automate Flows',
      description: 'Create automated workflows that connect your favorite apps and services seamlessly.'
    },
    {
      icon: FiFolder,
      title: 'SharePoint Integration',
      description: 'Automate document management, approvals, and collaboration workflows in SharePoint.'
    },
    {
      icon: FiUsers,
      title: 'Teams Automation',
      description: 'Streamline team communication and collaboration with automated notifications and workflows.'
    },
    {
      icon: FiMail,
      title: 'Outlook Workflows',
      description: 'Automate email processing, calendar management, and task creation in Outlook.'
    },
    {
      icon: FiSettings,
      title: 'Power Platform Integration',
      description: 'Leverage the full Microsoft Power Platform ecosystem for comprehensive automation.'
    }
  ];

  const benefits = [
    'Integrate with 500+ Microsoft connectors',
    'Reduce manual tasks by 80%',
    'Improve team collaboration efficiency',
    'Automate approval processes',
    'Sync data across Microsoft applications',
    'Scale automation across your organization'
  ];

  return (
    <div className="min-h-screen bg-white pt-16">
      {/* Header */}
      <section className="bg-gradient-to-br from-indigo-600 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-8">
            <Link to="/" className="flex items-center space-x-2 text-indigo-200 hover:text-white transition-colors">
              <SafeIcon icon={FiArrowLeft} className="h-5 w-5" />
              <span>Back to Services</span>
            </Link>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <SafeIcon icon={FiSettings} className="h-12 w-12 text-indigo-200" />
                <h1 className="text-4xl md:text-5xl font-bold">
                  Microsoft 365 Automation
                </h1>
              </div>
              <p className="text-xl text-indigo-100 mb-8 leading-relaxed">
                Leverage Microsoft Power Platform to automate workflows across Teams, SharePoint, Outlook, and more.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://tidycal.com/your-booking-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-indigo-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg inline-flex items-center justify-center"
                >
                  Book Free Consultation
                </a>
                <Link
                  to="/#contact"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white hover:text-indigo-600 transition-colors font-semibold text-lg inline-flex items-center justify-center"
                >
                  Get Quote
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2126&q=80"
                alt="Microsoft 365 automation"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Microsoft <span className="text-indigo-600">Power Platform</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Harness the full potential of Microsoft's automation and integration capabilities.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow"
              >
                <div className="bg-indigo-100 p-4 rounded-lg w-fit mb-6">
                  <SafeIcon icon={feature.icon} className="h-8 w-8 text-indigo-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-8">
                Maximize Your <span className="text-indigo-600">Microsoft Investment</span>
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center space-x-4"
                  >
                    <SafeIcon icon={FiCheck} className="h-6 w-6 text-green-500" />
                    <span className="text-lg text-gray-700">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
                alt="Team collaboration"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-white"
          >
            <h2 className="text-4xl font-bold mb-6">Ready to Automate with Microsoft?</h2>
            <p className="text-xl text-indigo-100 mb-8 leading-relaxed">
              Unlock the full potential of your Microsoft 365 environment with our automation solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://tidycal.com/your-booking-link"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-indigo-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg"
              >
                Book Free Demo
              </a>
              <Link
                to="/#contact"
                className="border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white hover:text-indigo-600 transition-colors font-semibold text-lg"
              >
                Get Custom Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MicrosoftAutomation;